/**
 * What a run is doing while it runs, and the button that stops it.
 *
 * A whole movement takes tens of seconds, and a board that says "Arranging…" that long cannot
 * be told from one that has hung. So the strip says which stage the pipeline last reported,
 * with the bars it is arranging once it knows them. A scan being read into a score is said
 * here too, by the pages it has gone through: it takes as long, and is stopped the same way.
 */

import type { RunProgress } from "../lib/arrange-job";

function bars(progress: RunProgress): string {
  if (!("first" in progress) || progress.first === undefined || progress.last === undefined) {
    return "";
  }
  return ` bars ${progress.first}–${progress.last}`;
}

/** The stage as a sentence. */
export function saying(progress: RunProgress): string {
  switch (progress.stage) {
    case "uploading":
      return "Sending the score to the arranger…";
    case "queued":
      return "Starting the arranger…";
    case "cut":
      return `Cut${bars(progress)} out of the score; reading them next…`;
    case "planning":
      return `The AI arranger is planning${bars(progress) || " the score"}…`;
    case "reading":
      return `Reading${bars(progress) || " the score"} and handing its notes to the players…`;
    case "writing":
      return `The note model is writing${bars(progress) || " the score"} under the tune…`;
    case "look":
      return progress.page === undefined || progress.pages === undefined
        ? "Looking through the scan for its pages of music…"
        : `Looking at page ${progress.page} of ${progress.pages} of the scan for its music…`;
    case "scan":
      if (progress.pages === undefined) return "Opening the scan…";
      if (progress.done !== undefined) {
        return `Reading the scan: ${progress.done} of its ${progress.pages} pages read…`;
      }
      return progress.page === undefined
        ? `Reading the scan's ${progress.pages} pages…`
        : `Reading page ${progress.page} of the scan's ${progress.pages}…`;
  }
}

/** Photos of pages being read into a score, as a sentence: the page being read once known. */
export function sayingPhotos(progress: RunProgress): string {
  if (progress.stage === "uploading") return "Sending the photos…";
  if (progress.stage !== "scan" || progress.pages === undefined) return "Reading the photos…";
  // Audiveris says how many pages are through, any other reader the page it begins.
  const page = progress.done === undefined ? progress.page : progress.done + 1;
  if (page === undefined) return "Reading the photos…";
  return `Reading the photos… page ${Math.min(page, progress.pages)} of ${progress.pages}`;
}

export default function ArrangeProgress({
  progress,
  onCancel,
}: {
  readonly progress: RunProgress;
  onCancel(): void;
}): JSX.Element {
  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-3 border-b border-shell-edge bg-shell-bar px-2.5 py-1.5 text-[11px] text-shell-text"
    >
      <span className="min-w-0 flex-1 truncate">{saying(progress)}</span>
      <button
        type="button"
        onClick={onCancel}
        className="h-6 shrink-0 rounded border border-shell-seam px-2.5 text-[11px] text-shell-text hover:bg-shell-key"
      >
        Cancel
      </button>
    </div>
  );
}
